import { useQuery } from '@tanstack/react-query';

import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { getRequest } from 'src/utils/request';

import { daysUntilToday } from 'src/utils/datetime';
import { keyBy } from 'lodash';
import { buildAttributes, buildChild, buildChildren } from 'src/utils/tools';

const getCandidateDetails = async (uuid?: string) => {
  const { data } = await getRequest(API.GET.getCandidate(uuid));

  return {
    data: mapCandidate(data?.data, data.included) as UCM.Candidate,
    included: data.included,
  };
};

export const useGetCandidateDetails = ({
  uuid,
  enabled = true,
}: {
  uuid?: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.CANDIDATE_DETAILS, uuid],
    queryFn: () => getCandidateDetails(uuid),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
};
// eslint-disable-next-line
const mapCandidate = (data: any, included: any) => {
  if (!data) return [];

  const paresedIncluded = keyBy(included, (item) => `${item.id}-${item.type}`);

  return buildCandidates(data, paresedIncluded);
};
// eslint-disable-next-line
export function findAttribute(parent: any, attributeName: string) {
  if (!parent.attributes) return null;
  if (!parent.attributes[attributeName]) return null;
  return parent.attributes[attributeName];
}
// eslint-disable-next-line
export function buildCandidates(data: any, included: any) {
  return buildAttributes(data, {
    email: 'email',
    lastName: 'last_name',
    firstName: 'first_name',
    rating: 'ratings_score',
    deletedAt: 'deleted_at',
    birthday: 'birth_date',
    birthName: 'birth_name',
    ratedJobs: 'rated_jobs',
    birthPlace: 'birth_place',
    taxClassCode: 'tax_class_code',
    birthPostCode: 'birth_post_code',
    hasDrivingLicense: 'has_driving_license',
    averageJobPerformanceRating: 'average_job_performance_rating',
    workdaysCompleted: 'workdays_completed',
    applicationsBookedRatio: 'applications_booked_ratio',
    applicationsCancelledRatio: 'applications_cancelled_ratio',
    briefingsConfirmedRatio: 'briefings_confirmed_ratio',
    confirmationTime: 'confirmation_time',
    prescreeningRating: 'prescreening_rating',
    punctualityRatio: 'punctuality_ratio',
    timelyCancellationsRatio: 'timely_cancellations_ratio',
    unjustifiedCancellationsRatio: 'unjustified_cancellations_ratio',
    justifiedCancellationsRatio: 'justified_cancellations_ratio',
    workdaysLeft: 'workdays_left',
    lastJobShift: () =>
      buildChild(data, included, 'last_working_day', null, {
        jobDate: (jobShift) =>
          buildChild(jobShift, included, 'job_date', null, {
            job: (jobDate) =>
              buildChild(jobDate, included, 'job', null, {
                title: 'title',
                jobType: (innerJob) =>
                  buildChild(innerJob, included, 'job_type'),
                project: (innerJob) =>
                  buildChild(innerJob, included, 'project'),
              }),
            startsAt: 'starts_at',
            endsAt: 'ends_at',
          }),
        age: (jobShift) => {
          const lastShift = findAttribute(jobShift, 'last_shift');
          return daysUntilToday(lastShift);
        },
      }),
    pastJobs: () => data?.meta?.completed_works_count ?? null,
    userLanguages: () =>
      buildChildren(data, included, 'user_preferred_languages', null, {
        language: (userLanguage) =>
          buildChild(userLanguage, included, 'language'),
        status: 'status',
      }),
    certificates: () =>
      buildChildren(data, included, 'certificates', null, {
        code: 'code',
        title: 'title',
      }),
    gender: () =>
      buildChild(data, included, 'gender', null, {
        code: 'code',
        name: 'name',
      }),
    status: () =>
      buildChild(data, included, 'status', null, {
        code: 'code',
        name: 'name',
      }),
    jobTypes: (innerUser) =>
      buildChildren(
        innerUser,
        included,
        'user_preferred_job_types',
        'job_type',
        {
          code: 'code',
          maleTitle: 'male_title',
          femaleTitle: 'female_title',
        },
      ),
    cities: (innerUser) =>
      buildChildren(innerUser, included, 'user_desired_cities', 'city', {
        code: 'code',
        name: 'name',
      }),
    languages: (innerUser) =>
      buildChildren(
        innerUser,
        included,
        'user_preferred_languages',
        'language',
        {
          code: 'code',
          name: 'name',
        },
      ),
    device: (innerUser) =>
      buildChild(innerUser, included, 'user_device', null, {
        appVersion: 'app_version',
        deviceOS: 'device_os',
      }),
    appLanguage: (innerUser) =>
      buildChild(innerUser, included, 'locale', null, {
        code: 'code',
        name: 'name',
      }),
    notification: (innerUser) =>
      buildChild(innerUser, included, 'notification', null, {
        standardJobUpdates: 'standard_job_updates',
        priorityJobs: 'priority_jobs',
      }),
    birthCountry: (innerUser) =>
      buildChild(innerUser, included, 'birth_country', null, {
        code: 'code',
        name: 'name',
      }),
    residence: (innerUser) =>
      buildChild(innerUser, included, 'user_residence_address', null, {
        streetName: 'street_name',
        streetNumber: 'street_number',
        postCode: 'post_code',
        country: 'country',
        town: 'town',
        lat: 'lat',
        lng: 'lng',
      }),
    phoneNumbers: (innerUser) =>
      buildChildren(innerUser, included, 'user_phone_numbers', null, {
        number: 'phone_number',
        callingCode: (phoneNumber) => {
          const callingCode = buildChild(
            phoneNumber,
            included,
            'calling_code',
            null,
            {
              code: 'code',
            },
          );
          return callingCode && callingCode.code;
        },
      }),
    healthInsurance: (innerUser) =>
      buildChildren(innerUser, included, 'user_insurances', null, {
        type: 'insurance_type',
        number: 'insurance_number',
        provider: 'insurance_provider',
      }),
    education: (innerUser) =>
      buildChild(innerUser, included, 'education', null, {
        fieldStudy: 'field_study',
        university: 'institution_name',
        educationLevel: (education) =>
          buildChild(education, included, 'education_level'),
        highestSchoolDegree: (education) =>
          buildChild(education, included, 'highest_school_degree'),
        address: (education) =>
          buildChild(education, included, 'education_address', null, {
            country: (address) => buildChild(address, included, 'country'),
            streetNumber: 'street_number',
            streetName: 'street_name',
            postCode: 'post_code',
            town: 'town',
            lat: 'lat',
            lng: 'lng',
          }),
      }),
    matriculationFiles: (innerUser) =>
      buildChildren(innerUser, included, 'user_matriculation_files', null, {
        validFrom: 'issue_date',
        validUntil: 'expiration_date',
        status: (file) => buildChild(file, included, 'status'),
      }),
    workPermitFiles: (innerUser) =>
      buildChildren(innerUser, included, 'user_work_permissions', null, {
        validFrom: 'issue_date',
        validUntil: 'expiration_date',
        status: (file) => buildChild(file, included, 'status'),
      }),
    healthCertificates: (innerUser) =>
      buildChildren(innerUser, included, 'user_certificate_files', null, {
        validFrom: 'issue_date',
        validUntil: 'expiration_date',
        status: (file) => buildChild(file, included, 'status'),
      }),
    payroll: (innerUser) =>
      buildChild(innerUser, included, 'user_payroll_info', null, {
        bic: 'bic',
        iban: 'iban',
        postCode: 'post_code',
        identityCard: 'identity_card',
        bankAccountOwner: 'bank_account_owner',
        socialSecurityNumber: 'social_security_number',
        isTaxedByCompany: 'is_taxed_by_company',
        streetNumber: 'street_number',
        roomNumber: 'room_number',
        streetName: 'street_name',
        taxId: 'tax_id_string',
        town: 'town',
        lat: 'lat',
        lng: 'lng',
        country: (payroll) =>
          buildChild(payroll, included, 'country', null, {
            code: 'code',
            isEU: 'is_eu',
            name: 'name',
          }),
        citizenshipCountry: (payroll) =>
          buildChild(payroll, included, 'citizenship_country', null, {
            code: 'code',
            isEU: 'is_eu',
            name: 'name',
          }),
        religion: (payroll) =>
          buildChild(payroll, included, 'religion', null, {
            code: 'code',
            name: 'name',
          }),
        spouseReligion: (payroll) =>
          buildChild(payroll, included, 'spouse_religion', null, {
            code: 'code',
            name: 'name',
          }),
        taxClass: (payroll) =>
          buildChild(payroll, included, 'tax_class', null, {
            code: 'code',
            name: 'name',
          }),
        contractType: (payroll) =>
          buildChild(payroll, included, 'user_contract_type', null, {
            code: 'code',
          }),
      }),
  });
}
